export const BRIDGE_SOURCE = 'nova-embed-bridge' as const;
export const PROTOCOL_VERSION = 1 as const;
export const LOADER_VERSION = '1.1.0';

export const HOST_APPS = [
  'accounts',
  'claims',
  'conversion',
  'files',
  'payments',
  'quoting',
  'trading-partners',
] as const;

export type HostApp = (typeof HOST_APPS)[number];
export type NovaDimension = 'panel' | 'panel-sidebar' | 'fullscreen';

export interface NovaLauncherSize {
  width: number;
  height: number;
}

export interface NovaHostContext {
  hostApp: HostApp;
  accountUuid?: string;
}

export interface NovaHostContextPatch {
  accountUuid?: string | null;
}

export interface HostVisibility {
  visible: boolean;
  focused: boolean;
}

export interface NovaBridgeEnvelope<TType extends string, TPayload> {
  source: typeof BRIDGE_SOURCE;
  protocolVersion: number;
  type: TType;
  payload: TPayload;
}

export type NovaReadyMessage = NovaBridgeEnvelope<'nova:ready', { embedVersion: string; capabilities: string[] }>;
export type NovaStateMessage = NovaBridgeEnvelope<
  'nova:state',
  {
    enabled: boolean;
    open: boolean;
    dimension: NovaDimension;
    hasNotifications: boolean;
    launcher?: NovaLauncherSize;
  }
>;
export type NovaLauncherMoveMessage = NovaBridgeEnvelope<'nova:launcher-move', { deltaX: number; persist: boolean }>;
export type NovaErrorMessage = NovaBridgeEnvelope<
  'nova:error',
  { code: 'unauthenticated' | 'internal'; message?: string }
>;

export type HostInitMessage = NovaBridgeEnvelope<
  'host:init',
  {
    loaderVersion: string;
    hostApp: HostApp;
    context: NovaHostContext;
    visibility: HostVisibility;
    open: boolean;
  }
>;
export type HostContextMessage = NovaBridgeEnvelope<'host:context', { patch: NovaHostContextPatch }>;
export type HostOpenMessage = NovaBridgeEnvelope<'host:open', Record<string, never>>;
export type HostCloseMessage = NovaBridgeEnvelope<'host:close', Record<string, never>>;
export type HostToggleMessage = NovaBridgeEnvelope<'host:toggle', Record<string, never>>;
export type HostVisibilityMessage = NovaBridgeEnvelope<'host:visibility', HostVisibility>;

export type NovaToHostMessage = NovaReadyMessage | NovaStateMessage | NovaLauncherMoveMessage | NovaErrorMessage;
export type HostToNovaMessage =
  | HostInitMessage
  | HostContextMessage
  | HostOpenMessage
  | HostCloseMessage
  | HostToggleMessage
  | HostVisibilityMessage;

export type NovaToHostMessageType = NovaToHostMessage['type'];
export type HostToNovaMessageType = HostToNovaMessage['type'];

type NovaBridgeMessage = NovaToHostMessage | HostToNovaMessage;

export function createBridgeMessage<TType extends NovaBridgeMessage['type']>(
  type: TType,
  payload: Extract<NovaBridgeMessage, { type: TType }>['payload'],
): Extract<NovaBridgeMessage, { type: TType }> {
  return {
    source: BRIDGE_SOURCE,
    protocolVersion: PROTOCOL_VERSION,
    type,
    payload,
  } as Extract<NovaBridgeMessage, { type: TType }>;
}
